import React from "react";
import { useTranslation } from "react-i18next";
import { titleCase } from "title-case";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import { Card, DataRow } from "@outpave/ds";
import AccountType from "enums/AccountType";
import Role from "enums/Role";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { Inline, Stack } from "components/layout/reusableLayouts";
import type { BankAccount } from "hooks/useGetBankAccounts";
import { EMPTY, MaskedDetailRow } from "components/common/detailRows";

interface AccountDetailsCardProps {
  account?: BankAccount;
  onWireInstructions: () => void;
}

/**
 * The selected account's identifying details. These are DataRows rather than a
 * Summary: the design draws single-line rows with a leading glyph and a
 * right-anchored value, which is the stat recipe, not the label-over-value
 * field grid a Summary renders.
 *
 * Every row renders even when its value is missing, so the card keeps its
 * height as the selected account changes.
 */
const AccountDetailsCard: React.FC<AccountDetailsCardProps> = ({
  account,
  onWireInstructions,
}) => {
  const { t } = useTranslation();
  const isAdminOrHigher = useCheckUserRole(Role.Admin);

  // Runtime data never goes through t(): i18next reads ":" as a namespace
  // separator and would eat part of the name.
  const cased = (value?: string) =>
    value ? titleCase(value.toLowerCase()) : EMPTY;

  const rows = [
    <DataRow
      key="account"
      label={t("Account")}
      primaryTrailing={cased(account?.nickname)}
    />,
    <DataRow
      key="bank"
      label={t("Bank name")}
      primaryTrailing={cased(account?.bankName)}
    />,
    <DataRow
      key="balance"
      label={t("Available balance")}
      primaryTrailing={account?.availableAmount ?? EMPTY}
    />,
    <MaskedDetailRow
      key="routing"
      label={t("Routing number")}
      value={account?.routingNumber}
      showLabel={t("Show routing number")}
      hideLabel={t("Hide routing number")}
      copyLabel={t("Copy routing number")}
      copiedMessage={t("Routing number copied to clipboard")}
      canCopy={isAdminOrHigher}
    />,
    <MaskedDetailRow
      key="number"
      label={t("Account number")}
      value={account?.accountNumber}
      showLabel={t("Show account number")}
      hideLabel={t("Hide account number")}
      copyLabel={t("Copy account number")}
      copiedMessage={t("Account number copied to clipboard")}
      canCopy={isAdminOrHigher}
    />,
  ];

  return (
    <Card
      variant="outlined"
      title={t("Account Details")}
      sx={{ height: "100%" }}
    >
      <Stack gap="var(--space-0)">
        {rows.map((row, index) => (
          <React.Fragment key={row.key}>
            {index > 0 && <Divider />}
            {row}
          </React.Fragment>
        ))}
      </Stack>

      {/* The dialog shows the numbers this card masks, so the entry point
          carries the same gate the values do. */}
      {isAdminOrHigher &&
        account?.accountType === AccountType.FINANCIAL_ACCOUNT && (
          <Inline>
            <Button variant="default-outlined" onClick={onWireInstructions}>
              {t("Wire instructions")}
            </Button>
          </Inline>
        )}
    </Card>
  );
};

export default AccountDetailsCard;
